import { Directive, ElementRef, computed, inject, input } from '@angular/core';
import {
  ACTION_SIZES,
  ACTION_TONES,
  ACTION_VARIANTS,
  type ActionSize,
  type ActionTone,
  type ActionVariant,
} from '@groove/core';

// The axis vocabularies are owned by @groove/core (framework-neutral, so the
// coverage engine can read them without pulling in @angular/core). Re-exported
// here so consumers can keep importing them from the directive.
export {
  ACTION_SIZES,
  ACTION_TONES,
  ACTION_VARIANTS,
  type ActionSize,
  type ActionTone,
  type ActionVariant,
};

@Directive({
  selector: 'button[grooveAction], a[grooveAction]',
  standalone: true,
  host: {
    class: 'groove-action groove-ring',
    '[attr.data-variant]': 'variantAttr()',
    '[attr.data-size]': 'sizeAttr()',
    '[attr.data-tone]': 'toneAttr()',
    '[attr.data-block]': 'block() ? "true" : null',
    '[attr.data-loading]': 'loading() ? "true" : null',
    '[attr.aria-busy]': 'loading() ? "true" : null',
    '[attr.aria-disabled]': 'disabled() ? "true" : null',
    '[attr.disabled]': 'buttonDisabled()',
  },
})
export class GrooveActionDirective {
  private readonly elementRef =
    inject<ElementRef<HTMLButtonElement | HTMLAnchorElement>>(ElementRef);

  readonly variant = input<ActionVariant>('solid');
  readonly size = input<ActionSize>('md');
  readonly tone = input<ActionTone>('accent');
  readonly block = input(false);
  readonly loading = input(false);
  readonly disabled = input(false);

  // Omit default-value attrs to keep the DOM clean.
  protected readonly variantAttr = computed(() =>
    this.variant() === 'solid' ? null : this.variant(),
  );
  protected readonly sizeAttr = computed(() =>
    this.size() === 'md' ? null : this.size(),
  );
  protected readonly toneAttr = computed(() =>
    this.tone() === 'accent' ? null : this.tone(),
  );

  // Native disabled attribute only on <button>; anchors use aria-disabled.
  protected readonly buttonDisabled = computed(() => {
    if (!this.disabled()) return null;
    return this.elementRef.nativeElement instanceof HTMLButtonElement
      ? ''
      : null;
  });
}
